import { Stack, Typography } from '@mui/material';
import { Form, Formik, FormikProps } from 'formik';
import { useCallback, useMemo, useRef, useState } from 'react';

import { FormPart } from '@modules/shared/components/FormPart';
import { queryParams } from '@shared/commonFunctions';
import { AlertModal } from '@shared/components/modals/AlertModal';
import { Modal } from '@shared/components/modals/Modal';
import { statesList } from '@shared/constants/statesList';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { TextInputField } from '@shared/fields/TextInputField';
import { CancelButton, SaveButton } from '@shared/inputs/Buttons';
import { CommonButtonWrapper } from '@styles/CommonStyles';

import { IBumperPlateResponse, IFormAsset, ILicensePlateProps, IBumperPlateToVin } from '../interfaces';
import { requestBumperPlateToVin } from '../requests';
import { plateToVinValidationSchema } from '../validation';

const BACKDROP_CLICK = 'backdropClick';
const ESCAPE_KEY_DOWN = 'escapeKeyDown';
const CLOSE_ICON = 'closeIcon';
const DUPLICATE_MESSAGE = 'Successful response already received for asset with the same license and state combination';
const NO_RESPONSE_MESSAGE = 'VIN lookup unsuccessful, no plate match found';
const UNSUCCESSFUL_RESPONSE_TITLE = 'Unsuccessful Response Received';
const SUCCESSFUL_RESPONSE_TITLE = 'Successful Response Received';
const ERROR_TITLE = 'Error';

const initialValues: IPlateInitial = {
  plateNumber: '',
  plateState: '',
};

const allertMessage = {
  title: '',
  text: '',
  on: false,
};

type IPlateInitial = Pick<IBumperPlateToVin, 'plateNumber' | 'plateState'>;

export const LicensePlateModal = ({
  isLicensePlateModalOpen,
  values,
  pullData,
  setIsLicensePlateModalOpen,
  onClose,
}: ILicensePlateProps) => {
  const params = queryParams();
  const formikRef = useRef<FormikProps<IPlateInitial> | null>(null);

  //const [plateInfo, setPlateInfo] = useState<IPlateToVinResp | null>(null);

  const [bumperPlateResponse, setBumperPlateResponse] = useState<IBumperPlateResponse | null>(null);
  const [currentVehicleIndex, setCurrentVehicleIndex] = useState<number>(0);
  const [loading, setLoading] = useState(false);

  const formConfig = useMemo(
    () => [
      {
        width: '100%',
        name: 'plateNumber',
        label: 'License Plate #',
        component: TextInputField,
        onChange: (ev: { target: { value: string } }) =>
          formikRef?.current?.setFieldValue('plateNumber', ev.target.value?.toUpperCase()),
      },
      {
        width: '100%',
        name: 'plateState',
        label: 'State',
        options: statesList,
        component: SelectAutocompleteField,
      },
    ],
    [formikRef?.current]
  );

  const setAllertMessage = useCallback((on: boolean, title = '', text = '') => {
    allertMessage.title = title;
    allertMessage.text = text;
    allertMessage.on = on;
  }, []);

  const handleSave = useCallback(
    async (data: IBumperPlateToVin) => {
      if (
        data?.plateNumber &&
        data?.plateState &&
        values?.assetRegistration?.plateNumber === data?.plateNumber &&
        values?.assetRegistration?.plateState === data?.plateState
      ) {
        setAllertMessage(true, ERROR_TITLE, DUPLICATE_MESSAGE);
        setLoading(false);
        onClose();
        return;
      }

      setLoading(true);

      const payload = {
        plateNumber: data?.plateNumber ?? '',
        plateState: data?.plateState ?? '',
      };

    const resp = await requestBumperPlateToVin(payload);
    resp &&
    setBumperPlateResponse({...resp, bumper_plate: resp.bumper_plate, plate: data?.plateNumber, state: data?.plateState});
    if (resp === null || resp?.is_error) setAllertMessage(true, UNSUCCESSFUL_RESPONSE_TITLE, NO_RESPONSE_MESSAGE);
    setLoading(false);
    onClose();
  }, [params]);

  const handleConfirm = useCallback(() => {
    const data = {
      ...values,
      vin: bumperPlateResponse?.bumper_plate?.entities?.vehicles?.automobiles[currentVehicleIndex]?.vin ?? '',
      isPlateToVinSuccess: bumperPlateResponse !== null && bumperPlateResponse?.is_error === false ? true : false,
      isVinLookupSuccess: true,
      assetRegistration: {
        ...values.assetRegistration,
        plateNumber: bumperPlateResponse?.plate ?? '',
        plateState: bumperPlateResponse?.state ?? '',
      },
    };
    pullData(data as IFormAsset);
    setBumperPlateResponse(null);
  }, [bumperPlateResponse, values, pullData]);

  const handleAlertConfirm = useCallback(() => {
    setBumperPlateResponse(null);
    setIsLicensePlateModalOpen(true);
  }, []);

  const handleClose = useCallback(
    (_: object, reason?: string) => {
      const isCloseReason = reason === BACKDROP_CLICK || reason === ESCAPE_KEY_DOWN || reason === CLOSE_ICON;

      if (!loading || isCloseReason) {
        onClose();
      }
    },
    [loading, onClose]
  );

  const renderAlert = useCallback(() => {
    const alertComponent = (
      <AlertModal
        hideCancel
        open
        title={allertMessage.title}
        buttonText="Ok"
        subtitle={allertMessage.text}
        onConfirm={handleAlertConfirm}
        onClose={() => setBumperPlateResponse(null)}
      />
    );
    setAllertMessage(false);
    return alertComponent;
  }, []);

  const handleArrayOfVehicles = useCallback(() => {
    const vehicles = bumperPlateResponse?.bumper_plate?.entities?.vehicles?.automobiles ?? [];
    if (vehicles?.length === 0) return;

    const currentVehicle = vehicles[currentVehicleIndex];
    const data = (
      <Stack
        key={currentVehicleIndex}
        alignItems="center"
      >
        <Typography variant="h3">VIN: {currentVehicle.vin ?? ''}</Typography>
        <Typography variant="h3">Year: {currentVehicle.year ?? ''}</Typography>
        <Typography variant="h3">Make: {currentVehicle.make ?? ''}</Typography>
        <Typography variant="h3">Model: {currentVehicle.model ?? ''}</Typography>
      </Stack>
    );
    const plate = (
      <Modal
        open
        title={SUCCESSFUL_RESPONSE_TITLE}
      >
        {data}
        <CommonButtonWrapper mt={2}>
          <CancelButton
            onClick={() => {
              if (currentVehicleIndex === vehicles?.length - 1) {
                setBumperPlateResponse(null);
              } else {
                setCurrentVehicleIndex(currentVehicleIndex + 1);
              }
            }}
          />
          <SaveButton
            label="Confirm"
            onClick={handleConfirm}
          />
        </CommonButtonWrapper>
      </Modal>
    );
    return plate;
  }, [bumperPlateResponse, currentVehicleIndex]);

  return (
    <>
      <Modal
        open={isLicensePlateModalOpen}
        title="Enter License Plate"
        dialogWidth="500px"
        onClose={(_, reason) => handleClose({}, reason)}
      >
        <Formik
          enableReinitialize
          initialValues={initialValues}
          innerRef={(f) => (formikRef.current = f)}
          validationSchema={plateToVinValidationSchema}
          onSubmit={handleSave}
        >
          {(props) => (
            <Form onSubmit={props.handleSubmit}>
              <FormPart config={formConfig} />
              <CommonButtonWrapper mt={2}>
                <CancelButton
                  disabled={loading}
                  onClick={onClose}
                />
                <SaveButton
                  label="Submit"
                  isLoading={loading}
                />
              </CommonButtonWrapper>
            </Form>
          )}
        </Formik>
      </Modal>

      {(bumperPlateResponse != null && !bumperPlateResponse.is_error && currentVehicleIndex >= 0) && 
        handleArrayOfVehicles()}

      {(allertMessage.on) && renderAlert()}
  
    </>
  );
};
